import { G } from '@mobily/ts-belt';
import CompletedIcon from '@mui/icons-material/CheckCircle';
import { FC, useCallback } from 'react';
import { TopToolbar, useNotify, useRecordContext, useRefresh } from 'react-admin';

import {
  GetSavingsFundsRequestDetailsEndpoint,
  PutSavingsFundsRequestEndpoint,
  SavingsFundsRequestAction,
} from '@Common/types/bo/b2b/savings_funds_requests';

import { Button } from '@sh4res/bo-common/components';
import { B2bSavingsFundRequestsPaymentCreate } from '@sh4res/bo-common/components/B2bSavingsFundRequests/B2bSavingsFundRequestsPayment/B2bSavingsFundRequestsPaymentCreate';
import { useResourceBasePath, useUpdatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

type ExtractedFundRequestData = {
  fundRequestId: number;
  isMarkedAsPaid: boolean;
  isMarkedAsCompleted: boolean;
  isMarkedAsCancelled: boolean;
  totalNetAmount: number;
  status: string | null;
};

type B2bSavingsFundsRequestActionsProps<T extends Record<string, unknown>> = {
  fundRequestIdField: keyof T;
  isMarkedAsPaidField: keyof T;
  isMarkedAsCompletedField: keyof T;
  isMarkedAsCancelledField: keyof T;
  totalNetAmountField: keyof T;
  statusField?: keyof T;
  record?: T;
};

export const B2bSavingsFundsRequestActionsInternal = <T extends Record<string, unknown>>({
  fundRequestIdField,
  isMarkedAsPaidField,
  isMarkedAsCompletedField,
  isMarkedAsCancelledField,
  totalNetAmountField,
  statusField,
  record: recordProp,
}: B2bSavingsFundsRequestActionsProps<T>) => {
  const contextRecord =
    useRecordContext<GetSavingsFundsRequestDetailsEndpoint['Client']['ResponseBody']>();

  const record = (recordProp ?? contextRecord) as T;
  const resourceBasePath = useResourceBasePath();
  const [update] = useUpdatePromise<PutSavingsFundsRequestEndpoint['Client']['RequestBody']>();
  const refresh = useRefresh();
  const notify = useNotify();
  const resource = `${resourceBasePath}savings_funds_requests` as const;

  const handleAction = useCallback(
    async (action: SavingsFundsRequestAction, fundRequestId: number) => {
      try {
        await update(resource, { id: fundRequestId, data: { action } });

        notify('resources.bo_common.b2b.savings_funds_requests.payments.success', {
          type: 'success',
        });
        refresh();
      } catch (error: unknown) {
        notify('resources.bo_common.b2b.savings_funds_requests.payments.failure', {
          type: 'warning',
          messageArgs: { error: getErrorMessage(error) },
          multiLine: true,
        });
      }
    },
    [update, refresh, notify, resource],
  );

  if (G.isNullable(record)) {
    return null;
  }

  const fundRequestData = extractFundRequestData(record, {
    fundRequestId: fundRequestIdField,
    isMarkedAsPaid: isMarkedAsPaidField,
    isMarkedAsCompleted: isMarkedAsCompletedField,
    isMarkedAsCancelled: isMarkedAsCancelledField,
    totalNetAmount: totalNetAmountField,
    ...(statusField !== undefined && { status: statusField }),
  });
  if (!fundRequestData) {
    return null;
  }

  const {
    fundRequestId,
    isMarkedAsPaid,
    isMarkedAsCompleted,
    isMarkedAsCancelled,
    totalNetAmount,
    status,
  } = fundRequestData;

  if (isMarkedAsCancelled) {
    return null;
  }

  const isFailedStatus = status?.endsWith('_failed') ?? false;

  let actionButtonToShow: 'completed' | 'paid' | null = null;
  if (!isMarkedAsPaid) {
    actionButtonToShow = 'paid';
  } else if (!isMarkedAsCompleted && !isFailedStatus) {
    actionButtonToShow = 'completed';
  }

  if (actionButtonToShow === null) {
    return null;
  }

  return (
    <>
      {actionButtonToShow === 'paid' && (
        <B2bSavingsFundRequestsPaymentCreate
          resource={resource}
          payment_amount={totalNetAmount}
          fundRequestId={fundRequestId}
        />
      )}
      {actionButtonToShow === 'completed' && (
        <Button
          permissions={{ resource, action: 'edit' }}
          label="resources.bo_common.b2b.savings_funds_requests.payments.completed"
          onClick={async () => handleAction('completed', fundRequestId)}
          data-testid="BoExternalB2bSavingsFundRequestsAction_CompletedButton">
          <CompletedIcon />
        </Button>
      )}
    </>
  );
};

export const B2bSavingsFundsRequestActions: FC = () => (
  <TopToolbar>
    <B2bSavingsFundsRequestActionsInternal<
      GetSavingsFundsRequestDetailsEndpoint['Client']['ResponseBody']
    >
      fundRequestIdField="id"
      isMarkedAsPaidField="is_marked_as_paid"
      isMarkedAsCompletedField="is_marked_as_completed"
      isMarkedAsCancelledField="is_marked_as_cancelled"
      totalNetAmountField="total_net_amount"
      statusField="status"
    />
  </TopToolbar>
);

function extractFundRequestData<T extends Record<string, unknown>>(
  record: T,
  fieldMapping: {
    fundRequestId: keyof T;
    isMarkedAsPaid: keyof T;
    isMarkedAsCompleted: keyof T;
    isMarkedAsCancelled: keyof T;
    totalNetAmount: keyof T;
    status?: keyof T;
  },
): ExtractedFundRequestData | null {
  const fundRequestId = record[fieldMapping.fundRequestId];
  const isMarkedAsPaid = record[fieldMapping.isMarkedAsPaid];
  const isMarkedAsCompleted = record[fieldMapping.isMarkedAsCompleted];
  const isMarkedAsCancelled = record[fieldMapping.isMarkedAsCancelled];
  const totalNetAmount = record[fieldMapping.totalNetAmount];
  const status = fieldMapping.status !== undefined ? record[fieldMapping.status] : null;

  if (
    typeof fundRequestId !== 'number' ||
    typeof isMarkedAsPaid !== 'boolean' ||
    typeof isMarkedAsCompleted !== 'boolean' ||
    typeof isMarkedAsCancelled !== 'boolean' ||
    typeof totalNetAmount !== 'number' ||
    (status !== null && typeof status !== 'string')
  ) {
    return null;
  }

  return {
    fundRequestId,
    isMarkedAsPaid,
    isMarkedAsCompleted,
    isMarkedAsCancelled,
    totalNetAmount,
    status: status as string | null,
  };
}
